import { Callout } from "../components/Callout";
import { Card } from "../components/Card";
import { Skeleton } from "../components/Skeleton";
import { StatusPill } from "../components/StatusPill";
import type { StatusTone } from "../components/StatusPill";
import { VisuallyHidden } from "../components/VisuallyHidden";
import { describeBidOutcome, describeStanding } from "../lib/bid-outcome";
import { formatDisplayDateLong, formatInstantDate, formatMoney } from "../lib/format";
import styles from "./BidsPanel.module.css";
import type { SellerBid, SellerBidHistory } from "../api/types";

/**
 * Every bid the founder has placed, won or lost (canon § 14, APP-010).
 *
 * Nothing else on this desk shows a losing bid. The auction panel reports only tomorrow's
 * standing bid, and the placement history reports only the days a deal actually **ran** — so
 * before this panel a bid that did not win appeared on no surface at all, while canon § 5
 * moved that founder's credit twice for it. That is the gap this exists to close, and it is
 * why every row says where the money went rather than only what the bid was.
 *
 * Three things are load-bearing.
 *
 * **The day is the unit, and it is the day the bid competed *for*.** Canon § 7 makes each
 * daily auction independent and has founders bid today for tomorrow, so rows are grouped
 * under the display date they were bidding on — not the date they were placed. An amount
 * with no day attached is not an answer to anything.
 *
 * **Repeat bidding is the normal case, not a duplicate.** The same approved deal bidding on
 * three consecutive days is three results on three dated boards. Grouping by day is what
 * makes that read as what canon § 7 allows rather than as a list that has gone wrong; the
 * rows are never collapsed or de-duplicated.
 *
 * **A standing bid is not a result.** Finalization settles every bid in an auction, so a bid
 * that is still standing is one whose day has not been decided. It is set apart from the
 * settled rows — in words, in its own pill and by its own edge, never by colour alone — so a
 * founder is never told they lost a day that has not happened.
 *
 * There are no click figures here. Canon § 1 promises no sales, leads or impression count,
 * and what a placement returned belongs to the placement history, which carries core's own
 * `QUALIFIED_OUTBOUND_CLICKS` sentence with the number. A bid bought a position; it did not
 * buy an audience, and this panel does not imply one.
 *
 * Every date is a UTC calendar date and is labelled as one (canon § 8).
 */

export function BidsSkeleton() {
  return (
    <Card className={styles.panel}>
      <Skeleton width="14rem" height="2rem" />
      <Skeleton width="60%" height="1rem" />
      <Skeleton width="100%" height="4rem" />
    </Card>
  );
}

/** Consecutive rows sharing a display date. Core's order is preserved exactly. */
interface BidDay {
  displayDate: string;
  bids: SellerBid[];
}

/**
 * Groups the rows by display date **without reordering them**.
 *
 * Core sends newest display day first, then canon § 7's own within-day ranking. Re-sorting
 * here could only disagree with that order, so the grouping walks the list and starts a new
 * day whenever the date changes — which is exactly the shape of core's `ORDER BY`.
 */
export function groupBidsByDay(bids: readonly SellerBid[]): BidDay[] {
  const days: BidDay[] = [];
  for (const bid of bids) {
    const current = days[days.length - 1];
    if (current !== undefined && current.displayDate === bid.displayDate) {
      current.bids.push(bid);
    } else {
      days.push({ displayDate: bid.displayDate, bids: [bid] });
    }
  }
  return days;
}

function toneFor(bid: SellerBid): StatusTone {
  // Only a win is a positive result. A bid that did not place is an ordinary outcome of a
  // daily auction rather than a warning, and dressing it as one would suggest the founder
  // has something to fix. A standing bid has no result at all yet.
  return bid.outcome === "won" ? "positive" : "neutral";
}

export interface BidsPanelProps {
  history: SellerBidHistory;
}

export function BidsPanel({ history }: BidsPanelProps) {
  const { bids } = history;

  if (bids.length === 0) {
    return (
      <Callout title="You have not placed a bid yet">
        Every bid you place appears here — the days you win a position and the days you do
        not — with what happened to the credit it reserved. Tomorrow&rsquo;s auction is on the
        panel above.
      </Callout>
    );
  }

  const days = groupBidsByDay(bids);
  const standing = bids.filter((bid) => bid.outcome === "standing").length;
  const upTo = formatInstantDate(history.serverTime);

  return (
    <div className={styles.panel}>
      <Card tone="lead" className={styles.summary}>
        <p className={styles.headline}>
          <span className={`${styles.total} figure`}>{String(bids.length)}</span>
          <span className={styles.totalLabel}>
            {bids.length === 1 ? "bid" : "bids"} across{" "}
            <span className="figure">{String(days.length)}</span>{" "}
            {days.length === 1 ? "display day" : "display days"}
            {standing === 0 ? null : (
              <>
                , <span className="figure">{String(standing)}</span> still standing
              </>
            )}
          </span>
        </p>
        {/* Canon § 7, said once here rather than repeated on every repeated row. */}
        <p className={styles.note}>
          Each daily auction is independent, so a deal that bids on several days appears once
          for each of them. A bid always competes for the day it is filed under, never for the
          day it was placed on.
        </p>
        <p className={styles.scope}>
          Dates are display days in {history.timeZone} (canon&rsquo;s daily boundary), read up
          to {upTo ?? history.serverTime}.
        </p>
      </Card>

      <div className={styles.days}>
        {days.map((day) => (
          <BidDaySection key={day.displayDate} day={day} timeZone={history.timeZone} />
        ))}
      </div>
    </div>
  );
}

function BidDaySection({ day, timeZone }: { day: BidDay; timeZone: string }) {
  const headingId = `bid-day-${day.displayDate}`;

  return (
    <section className={styles.day} aria-labelledby={headingId}>
      {/*
        * The day and its zone are one text node, not a styled span beside a date. An
        * accessible name concatenates adjacent inline nodes *after trimming each*, so two
        * nodes would be announced as "…2026· UTC" — the same trap `DealEditor`'s actions
        * document. Canon § 8 makes the zone part of what this heading states, so it has to
        * survive being read aloud.
        */}
      <h3 className={styles.dayHeading} id={headingId}>
        {`${formatDisplayDateLong(day.displayDate)} · ${timeZone}`}
      </h3>
      <ul className={styles.rows} role="list">
        {day.bids.map((bid) => (
          <li key={bid.bidId}>
            <BidRow bid={bid} />
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * One bid, on one display day.
 *
 * The mark column carries the **amount**, where the placement history carries a rank. That
 * is the difference between the two panels: a placement is a position on a board, and a bid
 * is money a founder committed. A won bid still states the position it took, because that is
 * what the money bought.
 */
function BidRow({ bid }: { bid: SellerBid }) {
  const copy = describeBidOutcome(bid.outcome);
  const rank = bid.placement?.rank ?? null;
  const consumed = bid.placement?.winningBidCents ?? null;

  return (
    <Card
      as="article"
      className={`${styles.row} ${copy.settled ? styles.settled : styles.standing}`}
    >
      <div className={styles.head}>
        {/*
          * The whole spoken phrase is one node, and the visible figure is hidden from
          * assistive technology rather than read a second time. A visible amount followed by
          * a hidden fragment is announced as one run of trimmed text — the trap `DealEditor`
          * documents — and "$15" on its own says nothing about what the number is.
          */}
        <p className={styles.amount}>
          <VisuallyHidden>{`Bid of ${formatMoney(bid.amountCents)}`}</VisuallyHidden>
          <span className="figure" aria-hidden="true">
            {formatMoney(bid.amountCents)}
          </span>
        </p>

        <div className={styles.names}>
          <h4 className={styles.product}>{bid.deal.productName}</h4>
          {/*
           * What the bid *bought*, and only that. The verdict itself is on the pill and is
           * deliberately not repeated here: one row stating its outcome twice reads twice
           * to a screen reader, and the second reading adds nothing.
           */}
          {rank === null && consumed === null ? null : (
            <p className={styles.result}>
              {rank === null ? null : (
                <>
                  Position <span className="figure">{String(rank)}</span> in the Top 10 that
                  day
                </>
              )}
              {rank !== null && consumed !== null ? " · " : null}
              {consumed === null ? null : (
                <>
                  <span className="figure">{formatMoney(consumed)}</span> consumed from your
                  credit
                </>
              )}
            </p>
          )}
        </div>

        <p className={styles.pill}>
          <StatusPill label={copy.label} tone={toneFor(bid)} description="Bid outcome" />
        </p>
      </div>

      <p className={styles.why}>
        {copy.note}
        {bid.outcome === "standing" ? ` ${describeStanding(bid.auctionFinalized)}` : null}
      </p>
    </Card>
  );
}
